/**
 * Team Configurator - Real-time pricing calculator
 *
 * Prices come only from the JSON config tag ([data-team-configurator-config])
 * that team-configurator.liquid renders from theme settings (Pricing group in
 * config/settings_data.json). There are deliberately no fallback prices here:
 * a missing or broken config leaves the calculator inactive and logs an error
 * instead of quoting stale numbers.
 */

(function() {
  'use strict';

  // Populated from the section's JSON config tag in init()
  let PRICING = null;
  let TIERS = null;
  let LEASE_MIN_SEATS = null;

  function loadConfig() {
    const el = document.querySelector('[data-team-configurator-config]');
    if (!el) return false;
    try {
      const cfg = JSON.parse(el.textContent);
      PRICING = cfg.pricing;
      TIERS = cfg.tiers;
      LEASE_MIN_SEATS = cfg.leaseMinSeats;
      return Boolean(PRICING && PRICING.purchase && PRICING.lease && typeof PRICING.subscription === 'number' &&
        TIERS && typeof LEASE_MIN_SEATS === 'number');
    } catch (e) {
      return false;
    }
  }

  // State
  let state = {
    sweepSeats: 0,
    scullingSeats: 0,
    pricingModel: null
  };

  // DOM Elements
  let elements = {};

  // Initialize when DOM is ready
  document.addEventListener('DOMContentLoaded', init);

  function init() {
    // Read pricing/tier config rendered from theme settings
    if (!loadConfig()) {
      if (document.querySelector('.team-configurator')) {
        console.error('Team configurator: missing or invalid pricing config; calculator disabled.');
      }
      return;
    }

    // Prevent iOS Safari double-tap-to-zoom on configurator controls
    setupDoubleTapZoomGuard();

    // Cache DOM elements
    elements = {
      // Seat quantity inputs
      sweepSeatsInput: document.getElementById('sweep-seats-input'),
      scullingSeatsInput: document.getElementById('sculling-seats-input'),
      sweepPriceLabel: document.getElementById('sweep-price-label'),
      scullingPriceLabel: document.getElementById('sculling-price-label'),
      sweepSubtitle: document.getElementById('sweep-subtitle'),
      scullingSubtitle: document.getElementById('sculling-subtitle'),
      validationError: document.getElementById('validation-error'),

      // Pricing model radios and price displays
      pricingSection: document.getElementById('pricing-section'),
      pricingModelRadios: document.querySelectorAll('input[name="pricing_model"]'),
      leaseOption: document.getElementById('model-lease'),
      leaseOptionLabel: document.querySelector('label[for="model-lease"]'),
      purchaseOption: document.getElementById('model-purchase'),
      purchaseHardware: document.getElementById('purchase-hardware'),
      purchaseSubscription: document.getElementById('purchase-subscription'),
      leasePrice: document.getElementById('lease-price'),

      // Summary elements
      summaryConfig: document.getElementById('summary-config'),
      summaryModel: document.getElementById('summary-model'),
      summarySweepRow: document.getElementById('summary-sweep-row'),
      summarySweepLabel: document.getElementById('summary-sweep-label'),
      summarySweepHardware: document.getElementById('summary-sweep-hardware'),
      summaryScullingRow: document.getElementById('summary-sculling-row'),
      summaryScullingLabel: document.getElementById('summary-sculling-label'),
      summaryScullingHardware: document.getElementById('summary-sculling-hardware'),
      summarySubscriptionRow: document.getElementById('summary-subscription-row'),
      summarySubscription: document.getElementById('summary-subscription'),
      summaryTotal: document.getElementById('summary-total'),
      summaryPeriod: document.getElementById('summary-period'),

      // Contact button
      contactBtn: document.getElementById('contact-sales-btn')
    };

    // Bind event listeners
    bindEvents();

    // Check for URL parameters to pre-populate configuration
    applyUrlConfig();

    // Initial calculation
    updatePricing();
  }

  function applyUrlConfig() {
    const urlParams = new URLSearchParams(window.location.search);
    const config = urlParams.get('config');

    if (!config) return;

    // Parse config parameter (e.g., "2-", "4x", "8+", "4+", "4-")
    const configLower = config.toLowerCase();
    let configApplied = false;

    // Sweep configurations: 8+, 4+, 4-, 2-
    if (configLower === '8+' || configLower === '8') {
      state.sweepSeats = 8;
      if (elements.sweepSeatsInput) elements.sweepSeatsInput.value = 8;
      configApplied = true;
    } else if (configLower === '4+' || configLower === '4-') {
      state.sweepSeats = 4;
      if (elements.sweepSeatsInput) elements.sweepSeatsInput.value = 4;
      configApplied = true;
    } else if (configLower === '2-' || configLower === '2') {
      state.sweepSeats = 2;
      if (elements.sweepSeatsInput) elements.sweepSeatsInput.value = 2;
      configApplied = true;
    }
    // Sculling configurations: 4x, 2x, 1x
    else if (configLower === '4x') {
      state.scullingSeats = 4;
      if (elements.scullingSeatsInput) elements.scullingSeatsInput.value = 4;
      configApplied = true;
    } else if (configLower === '2x') {
      state.scullingSeats = 2;
      if (elements.scullingSeatsInput) elements.scullingSeatsInput.value = 2;
      configApplied = true;
    } else if (configLower === '1x') {
      state.scullingSeats = 1;
      if (elements.scullingSeatsInput) elements.scullingSeatsInput.value = 1;
      configApplied = true;
    }

    // Auto-scroll to pricing section if config was applied
    if (configApplied && elements.pricingSection) {
      setTimeout(function() {
        elements.pricingSection.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 100);
    }
  }

  function setupDoubleTapZoomGuard() {
    const root = document.querySelector('[id^="TeamConfigurator-"]');
    if (!root) return;

    let lastTouchEndTime = 0;
    let lastTouchEndTarget = null;

    root.addEventListener(
      'touchend',
      function(e) {
        const target = e.target;
        if (!(target instanceof Element)) return;

        const shouldGuard = target.closest(
          '.configurator__quantity-btn, .configurator__option label, .configurator__summary-button'
        );
        if (!shouldGuard) return;
        if (target.closest('input, textarea, select')) return;

        const now = Date.now();
        const isFastDoubleTap = now - lastTouchEndTime <= 350;
        const isSameControl =
          lastTouchEndTarget &&
          (lastTouchEndTarget === shouldGuard || lastTouchEndTarget.contains(target) || shouldGuard.contains(lastTouchEndTarget));

        if (isFastDoubleTap && isSameControl) {
          e.preventDefault();
        }

        lastTouchEndTime = now;
        lastTouchEndTarget = shouldGuard;
      },
      { passive: false }
    );
  }

  function bindEvents() {
    // Sweep seats quantity buttons
    const sweepButtons = document.querySelectorAll('.configurator__quantity-btn[data-type="sweep"]');
    sweepButtons.forEach(btn => {
      btn.addEventListener('click', function() {
        const action = this.dataset.action;
        let value = parseInt(elements.sweepSeatsInput.value) || 0;

        if (action === 'increase' && value < 100) {
          value++;
        } else if (action === 'decrease' && value > 0) {
          value--;
        }

        elements.sweepSeatsInput.value = value;
        state.sweepSeats = value;
        updatePricing();
      });
    });

    // Sculling seats quantity buttons
    const scullingButtons = document.querySelectorAll('.configurator__quantity-btn[data-type="sculling"]');
    scullingButtons.forEach(btn => {
      btn.addEventListener('click', function() {
        const action = this.dataset.action;
        let value = parseInt(elements.scullingSeatsInput.value) || 0;

        if (action === 'increase' && value < 100) {
          value++;
        } else if (action === 'decrease' && value > 0) {
          value--;
        }

        elements.scullingSeatsInput.value = value;
        state.scullingSeats = value;
        updatePricing();
      });
    });

    // Sweep seats input change
    if (elements.sweepSeatsInput) {
      elements.sweepSeatsInput.addEventListener('input', function() {
        const value = parseInt(this.value) || 0;
        state.sweepSeats = Math.min(Math.max(value, 0), 100);
        this.value = state.sweepSeats;
        updatePricing();
      });
    }

    // Sculling seats input change
    if (elements.scullingSeatsInput) {
      elements.scullingSeatsInput.addEventListener('input', function() {
        const value = parseInt(this.value) || 0;
        state.scullingSeats = Math.min(Math.max(value, 0), 100);
        this.value = state.scullingSeats;
        updatePricing();
      });
    }

    // Pricing model change
    elements.pricingModelRadios.forEach(radio => {
      radio.addEventListener('change', function() {
        state.pricingModel = this.value;
        updatePriceLabels();
        updatePricing();

        // On mobile, scroll to contact button after selecting a plan
        if (window.innerWidth <= 768 && elements.contactBtn) {
          setTimeout(function() {
            elements.contactBtn.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }, 100);
        }
      });
    });

    // Quick-add buttons (add to current count, except clear which sets to 0)
    const quickButtons = document.querySelectorAll('.configurator__quick-btn');
    quickButtons.forEach(btn => {
      btn.addEventListener('click', function() {
        const type = this.dataset.type;
        const value = parseInt(this.dataset.value);
        const isClear = this.classList.contains('configurator__quick-btn--clear');

        if (type === 'sweep') {
          state.sweepSeats = isClear ? 0 : Math.min(state.sweepSeats + value, 100);
          elements.sweepSeatsInput.value = state.sweepSeats;
        } else if (type === 'sculling') {
          state.scullingSeats = isClear ? 0 : Math.min(state.scullingSeats + value, 100);
          elements.scullingSeatsInput.value = state.scullingSeats;
        }

        updatePricing();
      });
    });
  }

  function updatePriceLabels() {
    if (!state.pricingModel) return;

    const sweepPrice = PRICING[state.pricingModel].sweep;
    const scullingPrice = PRICING[state.pricingModel].sculling;
    const suffix = state.pricingModel === 'lease' ? '/seat/yr' : '/seat';

    if (elements.sweepPriceLabel) {
      elements.sweepPriceLabel.textContent = formatCurrency(sweepPrice) + suffix;
    }
    if (elements.scullingPriceLabel) {
      elements.scullingPriceLabel.textContent = formatCurrency(scullingPrice) + suffix;
    }
  }

  function calculateBoatBreakdown(seats, type) {
    const boats = [];
    let remaining = seats;

    if (type === 'sweep') {
      // Sweep boats: 8+, 4+, 2-
      const sizes = [
        { size: 8, label: '8+' },
        { size: 4, label: '4+' },
        { size: 2, label: '2-' }
      ];

      for (const boat of sizes) {
        const count = Math.floor(remaining / boat.size);
        if (count > 0) {
          boats.push({ count, label: boat.label });
          remaining = remaining % boat.size;
        }
      }
    } else {
      // Sculling boats: 4x, 2x, 1x
      const sizes = [
        { size: 4, label: '4x' },
        { size: 2, label: '2x' },
        { size: 1, label: '1x' }
      ];

      for (const boat of sizes) {
        const count = Math.floor(remaining / boat.size);
        if (count > 0) {
          boats.push({ count, label: boat.label });
          remaining = remaining % boat.size;
        }
      }
    }

    return boats;
  }

  function updateBoatBadges() {
    // Update sweep subtitle
    if (elements.sweepSubtitle) {
      if (state.sweepSeats === 0) {
        elements.sweepSubtitle.textContent = '1 monitor + 1 smart oarlock';
      } else {
        const sweepBoats = calculateBoatBreakdown(state.sweepSeats, 'sweep');
        const boatText = sweepBoats.map(boat => `${boat.count}× ${boat.label}`).join(' and ');
        elements.sweepSubtitle.textContent = `Equips ${boatText}`;
      }
    }

    // Update sculling subtitle
    if (elements.scullingSubtitle) {
      if (state.scullingSeats === 0) {
        elements.scullingSubtitle.textContent = '1 monitor + 2 smart oarlocks';
      } else {
        const scullingBoats = calculateBoatBreakdown(state.scullingSeats, 'sculling');
        const boatText = scullingBoats.map(boat => `${boat.count}× ${boat.label}`).join(' and ');
        elements.scullingSubtitle.textContent = `Equips ${boatText}`;
      }
    }
  }

  function updatePricingModelDisplays() {
    const totalSeats = state.sweepSeats + state.scullingSeats;

    // Calculate purchase hardware (one-time)
    const purchaseHardware = (state.sweepSeats * PRICING.purchase.sweep) +
                              (state.scullingSeats * PRICING.purchase.sculling);

    // Calculate purchase subscription (annual)
    const purchaseSubscription = totalSeats * PRICING.subscription;

    // Calculate lease annual cost (all-inclusive)
    const leaseAnnual = (state.sweepSeats * PRICING.lease.sweep) +
                        (state.scullingSeats * PRICING.lease.sculling);

    // Update display elements
    if (elements.purchaseHardware) {
      elements.purchaseHardware.textContent = totalSeats > 0 ? formatCurrency(purchaseHardware) : '--';
    }
    if (elements.purchaseSubscription) {
      elements.purchaseSubscription.textContent = totalSeats > 0 ? formatCurrency(purchaseSubscription) : '--';
    }
    if (elements.leasePrice) {
      const leaseAvailable = totalSeats >= LEASE_MIN_SEATS;
      elements.leasePrice.textContent = leaseAvailable ? formatCurrency(leaseAnnual) : '--';
    }
  }

  function updateLeaseAvailability(totalSeats) {
    const leaseAvailable = totalSeats >= LEASE_MIN_SEATS;

    if (elements.leaseOption) {
      elements.leaseOption.disabled = !leaseAvailable;
    }
    if (elements.leaseOptionLabel) {
      elements.leaseOptionLabel.classList.toggle('is-disabled', !leaseAvailable);
    }

    // If lease is selected but no longer available, switch to purchase
    if (!leaseAvailable && state.pricingModel === 'lease') {
      state.pricingModel = 'purchase';
      if (elements.purchaseOption) {
        elements.purchaseOption.checked = true;
      }
    }
  }

  function updatePricing() {
    const totalSeats = state.sweepSeats + state.scullingSeats;

    // Validate: must have at least 1 seat
    const hasSeats = totalSeats >= 1;
    const hasPlan = state.pricingModel !== null;
    const isValid = hasSeats && hasPlan;

    if (elements.validationError) {
      elements.validationError.classList.toggle('is-visible', !isValid);
    }
    if (elements.contactBtn) {
      elements.contactBtn.style.opacity = isValid ? '1' : '0.5';
      elements.contactBtn.style.pointerEvents = isValid ? 'auto' : 'none';
    }

    // Disable pricing section if no seats
    if (elements.pricingSection) {
      elements.pricingSection.classList.toggle('is-disabled', !hasSeats);
    }

    // Update lease availability (min 8 seats)
    updateLeaseAvailability(totalSeats);

    // Calculate costs (use purchase as fallback for display)
    const pricingKey = state.pricingModel || 'purchase';
    const sweepCost = state.sweepSeats * PRICING[pricingKey].sweep;
    const scullingCost = state.scullingSeats * PRICING[pricingKey].sculling;
    const subscriptionCost = state.pricingModel === 'purchase' ? totalSeats * PRICING.subscription : 0;

    // Year 1 total: equipment + subscription (Pro) or annual cost (Elite)
    const total = sweepCost + scullingCost + subscriptionCost;

    // Update summary display
    updateSummaryDisplay({
      sweepSeats: state.sweepSeats,
      scullingSeats: state.scullingSeats,
      totalSeats,
      pricingModel: state.pricingModel,
      sweepCost,
      scullingCost,
      subscriptionCost,
      total
    });

    // Update price labels
    updatePriceLabels();

    // Update boat badges
    updateBoatBadges();

    // Update pricing model option displays
    updatePricingModelDisplays();

    // Update contact button href with configuration
    updateContactButton();
  }

  function updateSummaryDisplay({ sweepSeats, scullingSeats, totalSeats, pricingModel, sweepCost, scullingCost, subscriptionCost, total }) {
    // Configuration summary
    let configText = '';
    if (sweepSeats > 0 && scullingSeats > 0) {
      configText = `${sweepSeats} sweep + ${scullingSeats} sculling (${totalSeats} total)`;
    } else if (sweepSeats > 0) {
      configText = `${sweepSeats} sweep seat${sweepSeats > 1 ? 's' : ''}`;
    } else if (scullingSeats > 0) {
      configText = `${scullingSeats} sculling seat${scullingSeats > 1 ? 's' : ''}`;
    } else {
      configText = 'No seats selected';
    }
    elements.summaryConfig.textContent = configText;

    // Pricing model
    elements.summaryModel.textContent = pricingModel ? TIERS[pricingModel] : 'Select a plan above';

    // Hide all pricing rows until a plan is selected
    if (!pricingModel) {
      elements.summarySweepRow.style.display = 'none';
      elements.summaryScullingRow.style.display = 'none';
      elements.summarySubscriptionRow.style.display = 'none';
      elements.summaryTotal.textContent = '--';
      elements.summaryPeriod.textContent = '';
      return;
    }

    // Update labels based on pricing model
    const isPurchase = pricingModel === 'purchase';
    if (elements.summarySweepLabel) {
      elements.summarySweepLabel.textContent = isPurchase
        ? 'Sweep Equipment'
        : `Sweep Seat${sweepSeats !== 1 ? 's' : ''}`;
    }
    if (elements.summaryScullingLabel) {
      elements.summaryScullingLabel.textContent = isPurchase
        ? 'Sculling Equipment'
        : `Sculling Seat${scullingSeats !== 1 ? 's' : ''}`;
    }

    // Sweep cost
    if (sweepSeats > 0) {
      elements.summarySweepRow.style.display = 'flex';
      elements.summarySweepHardware.textContent = isPurchase
        ? formatCurrency(sweepCost)
        : formatCurrency(sweepCost) + '/yr';
    } else {
      elements.summarySweepRow.style.display = 'none';
    }

    // Sculling cost
    if (scullingSeats > 0) {
      elements.summaryScullingRow.style.display = 'flex';
      elements.summaryScullingHardware.textContent = isPurchase
        ? formatCurrency(scullingCost)
        : formatCurrency(scullingCost) + '/yr';
    } else {
      elements.summaryScullingRow.style.display = 'none';
    }

    // Subscription row (purchase only)
    if (isPurchase && subscriptionCost > 0) {
      elements.summarySubscriptionRow.style.display = 'flex';
      elements.summarySubscription.textContent = formatCurrency(subscriptionCost) + '/yr';
    } else {
      elements.summarySubscriptionRow.style.display = 'none';
    }

    // Total
    elements.summaryTotal.textContent = formatCurrency(total);

    // Period indicator
    if (pricingModel === 'lease') {
      elements.summaryPeriod.textContent = '/yr';
    } else {
      elements.summaryPeriod.textContent = '';
    }
  }

  function updateContactButton() {
    if (!elements.contactBtn) return;

    // Remove any existing click handler and add new one
    elements.contactBtn.removeEventListener('click', handleContactClick);
    elements.contactBtn.addEventListener('click', handleContactClick);
  }

  function handleContactClick(e) {
    if (!state.pricingModel) return;

    e.preventDefault();

    const totalSeats = state.sweepSeats + state.scullingSeats;
    const modelLabel = TIERS[state.pricingModel];

    // Build configuration summary
    let configParts = [];
    if (state.sweepSeats > 0) {
      configParts.push(`${state.sweepSeats} sweep seat${state.sweepSeats > 1 ? 's' : ''}`);
    }
    if (state.scullingSeats > 0) {
      configParts.push(`${state.scullingSeats} sculling seat${state.scullingSeats > 1 ? 's' : ''}`);
    }

    const sweepCost = state.sweepSeats * PRICING[state.pricingModel].sweep;
    const scullingCost = state.scullingSeats * PRICING[state.pricingModel].sculling;
    const subscriptionCost = state.pricingModel === 'purchase' ? totalSeats * PRICING.subscription : 0;
    const total = sweepCost + scullingCost + subscriptionCost;

    // Store configuration in localStorage
    const configData = {
      sweepSeats: state.sweepSeats,
      scullingSeats: state.scullingSeats,
      totalSeats: totalSeats,
      plan: modelLabel,
      pricingModel: state.pricingModel,
      total: total,
      configSummary: configParts.join(' + '),
      timestamp: Date.now()
    };

    localStorage.setItem('xboat_team_config', JSON.stringify(configData));

    // Navigate to the contact destination configured on the button
    window.location.href = elements.contactBtn.getAttribute('href') || '/#contact';
  }

  // Team prices are USD amounts (theme settings) that Shopify does not convert
  // per market, so they are always shown in USD on purpose.
  function formatCurrency(amount) {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  }

})();
